import React from "react"
import Divider from "@material-ui/core/Divider"
import IconButton from "@material-ui/core/IconButton"
import ListItemText from "@material-ui/core/ListItemText"
import Menu from "@material-ui/core/Menu"
import MenuItem from "@material-ui/core/MenuItem"
import Typography from "@material-ui/core/Typography"
import { ProjectViewModel } from "../api/project"
import { useGetProjectTypesQuery } from "../../organisations/api"
import { COLOR_LIGHT } from "../../constants"
import { makeStyles, Theme } from "@material-ui/core"
import { ChevronDown } from "react-feather"
import { COLOR_BLACK } from "../../constants"
import { useProjectMutations } from "../hooks/use-project-mutations"
import type { ProjectTypeViewModel } from "../../organisations/api/project-type"
import { useTranslation } from "react-i18next"

type ProjectTypeOptionToRender = {
  label: string
  value: string
}

type Translations = typeof defaultTranslations

type ProjectTypeAssignmentOptionsParams = {
  project: ProjectViewModel
}

const mapProjectTypeToOption = (projectType: ProjectTypeViewModel): ProjectTypeOptionToRender => ({
  label: projectType.type,
  value: projectType.id,
})

const useProjectTypeOptions = ({ project }: ProjectTypeAssignmentOptionsParams) => {
  const { data: projectTypes = [] } = useGetProjectTypesQuery(project.maintainerId, {
    skip: project.isUserProject,
    refetchOnMountOrArgChange: true,
  })

  const selectedProjectType = projectTypes.find(({ id }) => id === (project.projectTypeId || ""))
  const selectedProjectTypeLabel = selectedProjectType?.type || null
  const selectableProjectTypes = projectTypes.filter((option) => option.isVisibleInOptions)
  const isSelectedProjectTypeHidden = Boolean(selectedProjectType && !selectedProjectType.isVisibleInOptions)
  const hasManyProjectTypes = projectTypes.length > 1
  const hasOnlyOneSelectableProjectType = selectableProjectTypes.length === 1
  const shouldShowProjectTypeAsText =
    hasManyProjectTypes && hasOnlyOneSelectableProjectType && !isSelectedProjectTypeHidden
  const projectTypeOptionsToRender = selectableProjectTypes.map(mapProjectTypeToOption)

  return {
    selectedProjectTypeLabel,
    projectTypeOptionsToRender,
    hasProjectTypes: projectTypes.length > 0,
    shouldShowCreateProjectTypeTip: projectTypes.length === 1,
    shouldShowProjectTypeAsText,
  }
}

const ProjectTypeDropdown = ({ project, isReadOnly }: { project: ProjectViewModel; isReadOnly: boolean }) => {
  const { projectType: label, ...translations } = useTranslations()
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLDivElement>(null)
  const openMenu = (e: React.MouseEvent<HTMLDivElement>) => setAnchorEl(e.currentTarget)
  const closeMenu = () => setAnchorEl(null)
  const { updateStatus } = useProjectMutations()
  const {
    selectedProjectTypeLabel,
    projectTypeOptionsToRender,
    hasProjectTypes,
    shouldShowCreateProjectTypeTip,
    shouldShowProjectTypeAsText,
  } = useProjectTypeOptions({ project })

  if (project.isUserProject) return null
  if (!hasProjectTypes) return null

  if (shouldShowProjectTypeAsText || isReadOnly) {
    return (
      <>
        <Typography variant="body1" style={{ marginBottom: 8, marginTop: 16 }}>
          <strong>{label}</strong>
        </Typography>
        <Typography variant="body1" style={{ marginBottom: 16 }}>
          {selectedProjectTypeLabel}
        </Typography>
      </>
    )
  }

  const removeProjectType = () => updateStatus(project.id, { projectTypeId: null })
  const setProjectType = (id: string) => updateStatus(project.id, { projectTypeId: id })

  return (
    <>
      <Typography variant="body1" style={{ marginBottom: 4, marginTop: 16 }}>
        <strong>{label}</strong>
      </Typography>
      <div className={classes.button} role="button" onClick={openMenu}>
        <Typography variant="body1">{selectedProjectTypeLabel}</Typography>
        <IconButton className="arrowDownIcon">
          <ChevronDown size={16} color={COLOR_BLACK} />
        </IconButton>
      </div>
      <Menu
        id="status-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        PaperProps={{ style: { width: 280 } }}
        disablePortal
        keepMounted
      >
        {projectTypeOptionsToRender.map((menuItem) => {
          return (
            <MenuItem
              onClick={() => {
                if (menuItem.value === project.projectTypeId) return removeProjectType()
                else setProjectType(menuItem.value)
              }}
              key={menuItem.label}
              selected={menuItem.value === project.projectTypeId}
            >
              <ListItemText
                primary={menuItem.label}
                primaryTypographyProps={{
                  style: {
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  },
                }}
              />
            </MenuItem>
          )
        })}
        {shouldShowCreateProjectTypeTip && [
          <Divider key="divider" />,
          <MenuItem key="tip">
            <ListItemText
              primary={translations.createProjectTypeTip}
              primaryTypographyProps={{
                variant: "body2",
                color: "textSecondary",
                style: {
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                },
              }}
            />
          </MenuItem>,
        ]}
      </Menu>
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    height: theme.spacing(5),
    margin: theme.spacing(0, -1, 1, -1),
    paddingLeft: theme.spacing(),
    borderRadius: theme.spacing(),
    cursor: "pointer",
    transition: theme.transitions.create("background"),
    "&:hover": {
      background: COLOR_LIGHT,
      "& .arrowDownIcon": {
        opacity: 1,
      },
    },
    "& .arrowDownIcon": {
      opacity: 0,
      transition: theme.transitions.create("opacity"),
    },
  },
}))

const defaultTranslations = {
  projectType: "Project type",
  notSet: "Not set",
  createProjectTypeTip: "To create a new project type, go to the organization settings.",
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { t } = useTranslation("translation")

  return {
    projectType: t("projectType") || defaults.projectType,
    notSet: t("notSet") || defaults.notSet,
    createProjectTypeTip: t("createProjectTypeTip") || defaults.createProjectTypeTip,
  }
}

export default ProjectTypeDropdown
